/**
 * CalendarLegend component - Displays legend for calendar day types
 */

import React from 'react'
import { Typography } from 'antd'
import { useIntl } from 'react-intl'
import { MiniCalendarDay } from '../types'

const { Text } = Typography

type DayType = MiniCalendarDay['type']

interface CalendarLegendProps {
  /** Array of day types to show in legend */
  types: DayType[]
}

/**
 * CalendarLegend displays a legend showing what different day types mean
 * Only shows types that are actually used (excludes 'empty' and 'workday')
 */
export const CalendarLegend: React.FC<CalendarLegendProps> = ({ types }) => {
  const { formatMessage } = useIntl()

  // Filter out empty and workday types (not shown in legend)
  const usedTypes = types.filter((t) => t !== 'empty' && t !== 'workday')

  if (usedTypes.length === 0) return null

  // Get translated labels for each day type
  const getDayTypeLabel = (type: DayType): string => {
    switch (type) {
      case 'pto':
        return formatMessage({ id: 'energyLevel.calendar.pto' })
      case 'weekend':
        return formatMessage({ id: 'energyLevel.calendar.nonWorking' })
      case 'holiday':
        return formatMessage({ id: 'energyLevel.calendar.holiday' })
      default:
        return ''
    }
  }

  return (
    <div className="mini-calendar-legend">
      {usedTypes.map((type) => (
        <div key={type} className="mini-calendar-legend-item">
          <div className={`mini-calendar-legend-dot mini-calendar-legend-dot-${type}`} />
          <Text type="secondary" className="mini-calendar-legend-text">
            {getDayTypeLabel(type)}
          </Text>
        </div>
      ))}
    </div>
  )
}
